
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { formatDate } from "@/lib/utils";
import { ArrowUpCircle, Calendar, AlertTriangle, Clock, Package } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";

interface SubscriptionCardProps {
  subscriptionData: any;
  remainingPercentage: number;
  isExpired: boolean;
  isAboutToExpire: boolean;
  isUnlimited: boolean;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscriptionData,
  remainingPercentage,
  isExpired,
  isAboutToExpire,
  isUnlimited,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  if (!subscriptionData || !subscriptionData.package) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className={isMobile ? "text-lg" : ""}>{t("no_active_subscription")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className={`text-muted-foreground ${isMobile ? "text-sm" : ""}`}>
            {t("no_subscription_description")}
          </p>
        </CardContent>
        <CardFooter>
          <Button variant="default" className="mt-4" size={isMobile ? "sm" : "default"}>
            <ArrowUpCircle className="mr-2 h-4 w-4" />
            {t("upgrade_now")}
          </Button>
        </CardFooter>
      </Card>
    );
  }

  const packageData = subscriptionData.package;

  return (
    <Card>
      <CardHeader className={isMobile ? "pb-3" : ""}>
        <CardTitle className={`flex items-center gap-2 ${isMobile ? "text-lg" : ""}`}>
          <Package className={`${isMobile ? "h-4 w-4" : "h-5 w-5"}`} />
          {t("current_package")}
        </CardTitle>
        <div className="flex items-center justify-between">
          <CardDescription className={isMobile ? "text-xs" : ""}>
            {t("package_details")}
          </CardDescription>
          <Badge 
            variant={
              isExpired ? "destructive" : 
              subscriptionData.status === "active" ? "default" : 
              "secondary"
            }
            className={isMobile ? "text-xs py-0 px-2" : ""}
          >
            {isExpired ? t("expired") : t(subscriptionData.status)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className={`space-y-${isMobile ? "4" : "6"}`}>
        <div className={`grid gap-4 ${isMobile ? "grid-cols-1" : "md:grid-cols-2"}`}>
          <div>
            <p className={`${isMobile ? "text-xs" : "text-sm"} font-medium`}>{t("package_name")}</p>
            <p className={`${isMobile ? "text-base" : "text-xl"} font-bold`}>{packageData.name}</p>
          </div>
          
          <div>
            <p className={`${isMobile ? "text-xs" : "text-sm"} font-medium`}>{t("subscription_period")}</p>
            <div className="flex items-center gap-2">
              <Calendar className={`${isMobile ? "h-3 w-3" : "h-4 w-4"} text-muted-foreground`} />
              <p className={isMobile ? "text-xs" : ""}>
                {isUnlimited 
                  ? t("unlimited") 
                  : `${formatDate(subscriptionData.start_date)} - ${formatDate(subscriptionData.end_date)}`
                }
              </p>
            </div>
          </div>
        </div>
        
        {!isUnlimited && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Clock className={`${isMobile ? "h-3 w-3" : "h-4 w-4"} text-muted-foreground`} />
                <p className={`${isMobile ? "text-xs" : "text-sm"} font-medium`}>{t("subscription_time_remaining")}</p>
              </div>
              <p className={`${isMobile ? "text-xs" : "text-sm"}`}>{Math.round(remainingPercentage)}%</p>
            </div>
            <Progress value={remainingPercentage} />
          </div>
        )}
        
        {(isExpired || isAboutToExpire) && !isUnlimited && (
          <div className={`flex items-center gap-2 p-${isMobile ? "2" : "3"} bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-md`}>
            <AlertTriangle className={`${isMobile ? "h-4 w-4" : "h-5 w-5"} text-amber-600`} />
            <p className={`${isMobile ? "text-xs" : "text-sm"}`}>
              {isExpired 
                ? t("subscription_expired")
                : t("subscription_expiring_soon")}
            </p>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-end gap-2">
        <Button variant="outline" size={isMobile ? "sm" : "default"}>
          {isMobile ? t("cancel") : t("cancel_subscription")}
        </Button>
        <Button size={isMobile ? "sm" : "default"}>
          <ArrowUpCircle className="mr-2 h-4 w-4" />
          {isMobile ? t("upgrade") : t("upgrade_renew")}
        </Button>
      </CardFooter>
    </Card>
  );
};
